import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { MessageCircle, CheckCircle2, Loader2, Camera, X, Crosshair } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { sendFeedback } from "@/api/feedback";
import { cssSelectorFor, elementLabelFor, selectorMatchCount } from "./selector";
import { loadFilerName, saveFilerName, MAX_FILER_NAME } from "./filerName";
import { captureScreenshot, screenshotSupported } from "./screenshot";

const MAX_BODY = 5000;

type Status = "idle" | "sending" | "success" | "error";

/** In-progress dialog contents, preserved across a trip into element-pick mode. */
interface Draft {
  body: string;
  selector: string;
  /** Elements the selector matched when it was captured. 1 = unambiguous. */
  matches: number;
  label: string;
  screenshot: string | null;
  shotBytes: number;
}

/** True when focus is in a field where F/E should type, not trigger hotkeys. */
function isTypingTarget(el: EventTarget | null): boolean {
  const node = el as HTMLElement | null;
  if (!node || !node.tagName) return false;
  const tag = node.tagName.toLowerCase();
  return tag === "input" || tag === "textarea" || tag === "select" || node.isContentEditable;
}

/**
 * Floating "Send feedback" widget (mounted once at the app shell, every route
 * incl. pre-auth). Posts to the backend proxy (POST /api/feedback/); the QA
 * token stays server-side.
 *
 * Entry paths to page-level feedback: the floating button and the `F` hotkey.
 * Entry paths to element-pick mode (highlight + click an element, its CSS
 * selector rides along): the crosshair button above the FAB, the "Pick element"
 * button inside the dialog, and the `E` hotkey.
 *
 * The hotkeys were once the ONLY way in, and the only hint sat in the footer of
 * the already-open dialog — so you had to know to press `E` before opening.
 * Measured cost: 21 of 38 QA tickets carried a selector, and none of the seven
 * filed on Aug 6-7 did (BEN-141). Hence the two visible controls; the hotkeys
 * still work exactly as before.
 *
 * Optional one-shot screenshot attach (getDisplayMedia).
 */
export default function FeedbackWidget() {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [body, setBody] = useState("");
  const [status, setStatus] = useState<Status>("idle");
  const [selector, setSelector] = useState("");
  const [label, setLabel] = useState("");
  const [matches, setMatches] = useState(0);
  const [picking, setPicking] = useState(false);
  const [hoverRect, setHoverRect] = useState<DOMRect | null>(null);
  const [screenshot, setScreenshot] = useState<string | null>(null);
  const [shotBytes, setShotBytes] = useState(0);
  const [capturing, setCapturing] = useState(false);
  // Remembered across submissions (and across shared demo logins) so it is
  // typed once. Read lazily — localStorage may be unavailable in private mode.
  const [filerName, setFilerName] = useState(() => loadFilerName());

  const hoverElRef = useRef<Element | null>(null);

  const shortPath = `${location.pathname}${location.search}` || "/";
  const trimmed = body.trim();
  const sending = status === "sending";

  /**
   * What the user had typed/attached when they left the dialog to pick an
   * element. Picking closes the dialog, so without this a half-written message
   * would be lost the moment someone reached for "Pick element" — the control
   * would punish the very people using it. Restored on pick, and on Esc.
   */
  const draftRef = useRef<Draft | null>(null);

  const openModal = useCallback((draft: Partial<Draft> = {}) => {
    // Callers spread draftRef at the call site, so clearing here is safe and
    // stops a stale draft resurfacing in an unrelated later submission.
    draftRef.current = null;
    setSelector(draft.selector ?? "");
    setMatches(draft.matches ?? 0);
    setLabel(draft.label ?? "");
    setBody(draft.body ?? "");
    setScreenshot(draft.screenshot ?? null);
    setShotBytes(draft.shotBytes ?? 0);
    setStatus("idle");
    setOpen(true);
  }, []);

  // --- element pick mode ------------------------------------------------
  /**
   * Enter pick mode. `carryDraft` is set when we came from the open dialog, so
   * the in-progress message survives the round trip. setOpen(false) directly
   * (not handleOpenChange) deliberately skips the close-reset.
   */
  const startPicking = useCallback((carryDraft: Draft | null = null) => {
    draftRef.current = carryDraft;
    setOpen(false);
    setPicking(true);
  }, []);

  const exitPick = useCallback(() => {
    setPicking(false);
    setHoverRect(null);
    hoverElRef.current = null;
  }, []);

  // Is this node part of our own widget UI (overlay/banner/modal)? Skip it.
  const isOwnUi = (el: Element | null) => !!el?.closest("[data-feedback-ui]");

  useEffect(() => {
    if (!picking) return;

    const onMove = (e: MouseEvent) => {
      const el = document.elementFromPoint(e.clientX, e.clientY);
      if (!el || isOwnUi(el)) {
        setHoverRect(null);
        hoverElRef.current = null;
        return;
      }
      hoverElRef.current = el;
      setHoverRect(el.getBoundingClientRect());
    };

    const onClick = (e: MouseEvent) => {
      const el = document.elementFromPoint(e.clientX, e.clientY);
      if (isOwnUi(el)) return; // let our own UI work normally
      // Intercept so the underlying handler / navigation never fires.
      e.preventDefault();
      e.stopPropagation();
      const picked = el || hoverElRef.current;
      exitPick();
      const sel = cssSelectorFor(picked);
      openModal({
        ...draftRef.current,
        selector: sel,
        // Measured here, while the live DOM the selector refers to still exists.
        matches: selectorMatchCount(sel),
        label: elementLabelFor(picked),
      });
    };

    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        const draft = draftRef.current;
        exitPick();
        // Cancelling a pick we entered FROM the dialog returns you to the
        // dialog, message intact — Esc means "not that element", not "discard".
        if (draft) openModal(draft);
      }
    };

    // Capture phase so we beat the page's own click handlers.
    document.addEventListener("mousemove", onMove, true);
    document.addEventListener("click", onClick, true);
    document.addEventListener("keydown", onKey, true);
    return () => {
      document.removeEventListener("mousemove", onMove, true);
      document.removeEventListener("click", onClick, true);
      document.removeEventListener("keydown", onKey, true);
    };
  }, [picking, exitPick, openModal]);

  // --- F / E hotkeys ----------------------------------------------------
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      // Don't hijack typing, modifier combos, the open modal, or active picking.
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (isTypingTarget(e.target)) return;
      if (open) return;
      const k = e.key.toLowerCase();
      if (k === "f" && !picking) {
        e.preventDefault();
        openModal();
      } else if (k === "e" && !picking) {
        e.preventDefault();
        startPicking();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, picking, openModal, startPicking]);

  // --- submit -----------------------------------------------------------
  function handleOpenChange(next: boolean) {
    setOpen(next);
    if (!next) {
      window.setTimeout(() => {
        setBody("");
        setStatus("idle");
        setSelector("");
        setMatches(0);
        setLabel("");
        setScreenshot(null);
        setShotBytes(0);
      }, 200);
    }
  }

  async function handleAttachScreenshot() {
    if (capturing) return;
    setCapturing(true);
    const result = await captureScreenshot();
    setCapturing(false);
    if (!result) {
      // Denied / cancelled / unsupported — silent, feedback still submittable.
      return;
    }
    setScreenshot(result.dataUri);
    setShotBytes(result.bytes);
  }

  async function handleSubmit() {
    if (!trimmed || sending) return;
    setStatus("sending");
    const declaredName = filerName.trim();
    // Persist before the request: worth remembering even if this send fails.
    saveFilerName(declaredName);
    try {
      await sendFeedback({
        body: trimmed.slice(0, MAX_BODY),
        page_url: window.location.href,
        platform: "web",
        // The viewport is what makes a layout complaint reproducible; the
        // backend folds it into the forwarded body, which the QA portal renders.
        viewport_width: window.innerWidth,
        viewport_height: window.innerHeight,
        ...(selector ? { element_selector: selector, element_selector_matches: matches } : {}),
        ...(label ? { element_label: label } : {}),
        ...(declaredName ? { filer_name: declaredName } : {}),
        ...(screenshot ? { screenshot } : {}),
      });
      setStatus("success");
      toast.success("Thanks for the feedback!");
      window.setTimeout(() => setOpen(false), 900);
    } catch {
      setStatus("error");
    }
  }

  const kb = (key: string) => (
    <kbd className="rounded border border-border bg-muted px-1.5 py-0.5 text-[10px] font-medium">
      {key}
    </kbd>
  );

  return (
    <>
      {/* Element-pick overlay: highlight box (pointer-events:none) + instruction banner */}
      {picking && (
        <div data-feedback-ui className="pointer-events-none fixed inset-0 z-[60]">
          {hoverRect && (
            <div
              className="absolute rounded-sm bg-primary/10 ring-2 ring-primary transition-[top,left,width,height] duration-75"
              style={{
                top: hoverRect.top,
                left: hoverRect.left,
                width: hoverRect.width,
                height: hoverRect.height,
              }}
            />
          )}
          <div className="absolute left-1/2 top-4 -translate-x-1/2 rounded-full bg-foreground px-4 py-2 text-xs font-medium text-background shadow-lg">
            Click an element to give feedback on it · press Esc to cancel
          </div>
        </div>
      )}

      {/* Column anchored at the old FAB position, so the main bubble does not
          move; the crosshair stacks above it. Hidden while picking — the
          instruction banner owns the screen then, and Esc is the way out. */}
      {!picking && (
        <div
          data-feedback-ui
          data-fab-dock="feedback"
          /* `bottom` reads the same custom property the reserved gutter is
             derived from (`--fab-offset` → `--fab-gutter` in index.css), so the
             stack cannot move away from the space kept clear for it. */
          className="fixed right-[calc(1.25rem+env(safe-area-inset-right))] bottom-[var(--fab-offset)] z-50 flex flex-col items-center gap-2"
        >
          <Button
            type="button"
            size="icon"
            variant="secondary"
            aria-label="Give feedback on a specific element (press E)"
            title="Pick an element (E)"
            onClick={() => startPicking()}
            /* Hidden below sm. Picking an element by tapping it is a poor
               interaction on a phone, and the extra 44px of the stack was what
               put fixed chrome over an account row's Disconnect button at
               390px. `--fab-stack-height` in index.css drops to match, so the
               reserved gutter stays derived from the stack rather than guessed. */
            /* Below lg the stack docks in the phone header (index.css, item 11), so it is
               sized in px there: rem would double it at 200% text and overflow the bar. */
            className="hidden size-[32px] rounded-full border border-border shadow-md hover:shadow-lg sm:inline-flex lg:size-9"
          >
            <Crosshair className="size-[16px] lg:size-4" />
          </Button>

          <Button
            type="button"
            size="icon"
            aria-label="Send feedback (press F)"
            title="Send feedback (F)"
            onClick={() => openModal()}
            className="size-[40px] rounded-full shadow-lg hover:shadow-xl lg:size-12"
          >
            <MessageCircle className="size-[20px] lg:size-5" />
          </Button>
        </div>
      )}

      {/* FP-038: a dismiss mid-send hid the result, and the send landed anyway. */}
      <Dialog open={open} busy={sending} onOpenChange={handleOpenChange}>
        <DialogContent data-feedback-ui className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Send feedback</DialogTitle>
            <DialogDescription>
              Spotted a bug or have an idea? We read every message.
            </DialogDescription>
          </DialogHeader>

          {status === "success" ? (
            <DialogBody className="flex flex-col items-center gap-3 py-10 text-center">
              <CheckCircle2 className="text-success h-10 w-10" />
              <p className="text-sm font-medium">Thanks for the feedback!</p>
            </DialogBody>
          ) : (
            // min-w-0: DialogBody is a grid item (DialogContent is a grid) —
            // without it the nowrap selector's min-content widens the whole
            // track and the chip/footer spill past the dialog edge.
            <DialogBody className="min-w-0 space-y-3">
              {selector && (
                /* min-w-0 at every flex level: without it the <code> refuses to
                   shrink (min-width:auto) and stretches the pill — and the whole
                   dialog — past its bounds. Full selector stays readable via title. */
                <div className="flex min-w-0 items-center gap-2 overflow-hidden rounded-md bg-muted/60 px-3 py-2 text-xs">
                  <Crosshair className="text-primary h-3.5 w-3.5 shrink-0" />
                  <span className="text-muted-foreground shrink-0 whitespace-nowrap">
                    Feedback on:
                  </span>
                  {/* The label is what the QA ticket will actually show, so it
                      leads here too; the selector stays reachable via title. */}
                  <span
                    className="text-foreground min-w-0 flex-1 truncate font-medium"
                    title={selector}
                  >
                    {label || selector}
                  </span>
                </div>
              )}

              <div className="space-y-1.5">
                <Label htmlFor="feedback-body">What&apos;s up?</Label>
                <Textarea
                  id="feedback-body"
                  value={body}
                  onChange={(e) => {
                    setBody(e.target.value);
                    if (status === "error") setStatus("idle");
                  }}
                  maxLength={MAX_BODY}
                  rows={5}
                  disabled={sending}
                  placeholder="Describe the issue or idea..."
                  aria-invalid={status === "error"}
                />
              </div>

              {/* Precision controls. "Pick element" sits beside the screenshot
                  button so the two ways of saying "I mean THIS" are one row. */}
              <div className="flex flex-wrap items-center gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={sending}
                  onClick={() =>
                    startPicking({ body, selector, matches, label, screenshot, shotBytes })
                  }
                >
                  <Crosshair className="h-4 w-4" />
                  {selector ? "Pick a different element" : "Pick element"}
                </Button>

                {!screenshot && screenshotSupported() && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={sending || capturing}
                    onClick={handleAttachScreenshot}
                  >
                    {capturing ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Camera className="h-4 w-4" />
                    )}
                    {capturing ? "Capturing..." : "Attach screenshot"}
                  </Button>
                )}
              </div>

              {screenshot && (
                <div className="flex items-center gap-3 rounded-md border border-border p-2">
                  <img
                    src={screenshot}
                    alt="Attached screenshot"
                    className="h-12 w-20 rounded object-cover"
                  />
                  <span className="text-muted-foreground text-xs">
                    Screenshot attached ({Math.round(shotBytes / 1024)} KB)
                  </span>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon-sm"
                    aria-label="Remove screenshot"
                    className="ml-auto"
                    onClick={() => {
                      setScreenshot(null);
                      setShotBytes(0);
                    }}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              )}

              <p className="text-muted-foreground text-xs">
                Sending from: <span className="text-foreground font-medium">{shortPath}</span>
                <span className="text-muted-foreground">
                  {" "}
                  · {kb("F")} feedback · {kb("E")} pick element
                </span>
              </p>

              {/* Last in the dialog on purpose: the widget must still open onto
                  "What's up?" for ordinary users. The label states the payoff —
                  "Your name (optional)" alone gets skipped, and then three
                  reviewers on one shared demo login are indistinguishable. */}
              <div className="space-y-1.5 border-t border-border pt-3">
                {/* `block` overrides shadcn Label's `flex`, which otherwise
                    pushes the parenthetical into its own column and wraps the
                    sentence raggedly. */}
                <Label htmlFor="feedback-filer" className="block text-xs font-normal">
                  Your name, so we can follow up{" "}
                  <span className="text-muted-foreground">(optional, remembered)</span>
                </Label>
                <Input
                  id="feedback-filer"
                  value={filerName}
                  onChange={(e) => setFilerName(e.target.value)}
                  maxLength={MAX_FILER_NAME}
                  disabled={sending}
                  placeholder="e.g. Anthony"
                  className="h-8 text-sm"
                />
              </div>

              {status === "error" && (
                <p className="text-destructive text-sm" role="alert">
                  Couldn&apos;t send your feedback. Please try again.
                </p>
              )}
            </DialogBody>
          )}

          {status !== "success" && (
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => handleOpenChange(false)}
                disabled={sending}
              >
                Cancel
              </Button>
              <Button type="button" onClick={handleSubmit} disabled={!trimmed || sending}>
                {sending && <Loader2 className="h-4 w-4 animate-spin" />}
                {sending ? "Sending..." : "Send feedback"}
              </Button>
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
